import { useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "app/store";
import { useGetUIContentQuery } from "features/ui-content/endpoints";
import {
  uiContentLoaded,
  noticeUiContentLoaded,
  litigationUiContentLoaded,
} from "features/ui-content/slice";
import { GaiaConstants } from "utils";

/**
 * Hook to load UI content when current resource team changes
 * @param currentResourceTeam The current resource team name
 */
export const useUIContentLoader = (currentResourceTeam: string) => {
  const dispatch = useAppDispatch();

  // Get team settings to determine capabilities
  const teamSettings = useAppSelector(
    (state) => state.teamSettings.selectedTeamMainMetaData
  );
  const currentGuestTeam = useAppSelector(
    (state) => state.teamSettings.currentGuestTeam
  );
  const headerSelectedTeam = useAppSelector(
    (state) => state.audits.headerSelectedTeam
  );

  // Determine current team settings (for both members and guests)
  const currentTeamSettings = useMemo(() => {
    if (!currentResourceTeam) return undefined;

    // If user is guest, get from current guest team
    if (headerSelectedTeam === "guest") {
      return currentGuestTeam?.selectedTeamMainMetaData;
    }

    // If user is team member, get from team settings
    return teamSettings;
  }, [currentResourceTeam, headerSelectedTeam, currentGuestTeam, teamSettings]);

  // Determine team capabilities
  const { hasAuditCapability, hasNoticeCapability, hasLitigationCapability } =
    useMemo(() => {
      if (!currentTeamSettings)
        return {
          hasAuditCapability: false,
          hasNoticeOnlyCapability: false,
          hasLitigationCapability: false,
        };

      const hasAudit =
        currentTeamSettings.teamCustomAttributesSchema?.audit ||
        currentTeamSettings.enabledFeatures?.includes("AUDIT") ||
        currentTeamSettings.enabledFeatures?.includes("audit");

      const hasNotice =
        currentTeamSettings.teamCustomAttributesSchema?.notice ||
        currentTeamSettings.enabledFeatures?.includes("NOTICE") ||
        currentTeamSettings.enabledFeatures?.includes("notice");

      const hasLitigation =
        currentTeamSettings.teamCustomAttributesSchema?.litigation ||
        currentTeamSettings.enabledFeatures?.includes("LITIGATION") ||
        currentTeamSettings.enabledFeatures?.includes("litigation");

      return {
        hasAuditCapability: hasAudit,
        hasNoticeCapability: hasNotice,
        hasLitigationCapability: hasLitigation,
      };
    }, [currentTeamSettings]);

  // Fetch UI content based on capabilities
  const { data: auditUiContentData, isLoading: isLoadingAuditUiContent } =
    useGetUIContentQuery(
      { teamName: currentResourceTeam || "", page: "createaudit" },
      {
        skip:
          !currentResourceTeam ||
          !hasAuditCapability ||
          currentResourceTeam === GaiaConstants.PLACE_HOLDER,
      }
    );

  const { data: noticeUiContentData, isLoading: isLoadingNoticeUiContent } =
    useGetUIContentQuery(
      { teamName: currentResourceTeam || "", page: "notice" },
      {
        skip:
          !currentResourceTeam ||
          !hasNoticeCapability ||
          currentResourceTeam === GaiaConstants.PLACE_HOLDER,
      }
    );

  const {
    data: litigationUiContentData,
    isLoading: isLoadingLitigationUiContent,
  } = useGetUIContentQuery(
    { teamName: currentResourceTeam || "", page: "litigation" },
    {
      skip:
        !currentResourceTeam ||
        !hasLitigationCapability ||
        currentResourceTeam === GaiaConstants.PLACE_HOLDER,
    }
  );

  // Dispatch data to store when received
  useEffect(() => {
    if (auditUiContentData) {
      dispatch(uiContentLoaded(auditUiContentData));
    }
  }, [dispatch, auditUiContentData, currentResourceTeam]);

  useEffect(() => {
    if (noticeUiContentData) {
      dispatch(noticeUiContentLoaded(noticeUiContentData));
    }
  }, [dispatch, noticeUiContentData, currentResourceTeam]);

  useEffect(() => {
    if (litigationUiContentData) {
      dispatch(litigationUiContentLoaded(litigationUiContentData));
    }
  }, [dispatch, litigationUiContentData, currentResourceTeam]);

  return {
    isLoadingAuditUiContent,
    isLoadingNoticeUiContent,
    isLoadingLitigationUiContent,
    hasAuditCapability,
    hasNoticeCapability,
    hasLitigationCapability,
  };
};
